import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { GetItemDataByIdQuery } from 'packages/shared/definitions/types';

import { ActionButton, ButtonModel } from '@notino/react-styleguide';

import messages from '@containers/ProductDetailContainer/messages';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { useAddToCartCloudTracking } from '@hooks/useAddToCardCloudTracking';

import { LinkSharer } from '../../components/LinkSharer';
import { GTM_WISHLIST_LIST_NAME } from '../../constants';
import { WishlistHeader } from '../../header';
import { Container, ButtonWrapper, Spacer } from '../../styled';
import AnimatedProductList from '../AnimatedProductList';

import {
  trackWishlistPageView,
  useTrackToCartAsync,
  useTrackToCartFailedAsync,
} from './tracking';
import { useWishlistedProducts } from './useWishlistedProducts';

export interface IWishlistPagerProps {
  productIds: string[];
  pageSize: number;
  userId: string; // logged-in user
  owner?: string; // id of foreign shared wishlist. In this case productIds are from the shared wishlist and further sharing is disabled
}

export const WishlistPager: React.FC<IWishlistPagerProps> = ({
  productIds,
  userId,
  pageSize,
  owner,
}) => {
  const { spa_redirect_wishlist_to_pd } = useFeatureFlags();

  const {
    products,
    fetchMore,
    setProductWishlisted,
    loading,
    error,
    hasMore,
    isEmpty,
  } = useWishlistedProducts(productIds, !!owner, { pageSize });

  const trackAddToCartAsync = useTrackToCartAsync(
    productIds,
    GTM_WISHLIST_LIST_NAME
  );
  const addToCardCloudTracking = useAddToCartCloudTracking();
  const trackAddToCartFailedAsync = useTrackToCartFailedAsync(
    productIds,
    GTM_WISHLIST_LIST_NAME
  );

  const handleProductAdded = async (
    product: GetItemDataByIdQuery['vpProductById']
  ) => {
    await trackAddToCartAsync(product);

    addToCardCloudTracking({ quantity: 1, productCode: product.productCode });
  };

  return (
    <Container>
      <WishlistHeader
        isEmpty={isEmpty}
        showLoginText={!isEmpty && userId === '0' && !owner}
        isShared={!!owner}
      />

      {isEmpty || owner ? <Spacer isSmall={!!owner} /> : <LinkSharer />}

      {!isEmpty && (
        <AnimatedProductList
          products={products}
          isSharedWishlist={!!owner}
          showAddToCartModal={false}
          canSpaRedirect={spa_redirect_wishlist_to_pd}
          productItemProps={{
            GTMEventLabel: GTM_WISHLIST_LIST_NAME,
            sendGTM: true,
            noPriceIfUnavailable: true,
            showBuyButton: true,
            onProductAdded: handleProductAdded,
            onProductAddFailed: trackAddToCartFailedAsync,
            onClosingCartModal: trackWishlistPageView,
            setProductWishlisted,
            inSectionOfType: 'wishlist',
            wishlistActionLocation: 'wishlist',
          }}
        />
      )}

      {hasMore && (
        <ButtonWrapper>
          <ActionButton
            buttonStyle={ButtonModel.Styles.secondary}
            buttonSize={ButtonModel.Sizes.medium}
            loading={loading}
            error={Boolean(error)}
            onClick={fetchMore}
          >
            <FormattedMessage {...messages.showNext} />
          </ActionButton>
        </ButtonWrapper>
      )}
    </Container>
  );
};
